import { FALSE, TRUE, UNDEFINED } from "constants/primitives";
import { useEffect, useState } from "preact/hooks";

import useAppState from "./useAppState";
import fetchTCFGlobalVendorList from "helpers/fetchTCFGlobalVendorList";
import { ITCFGlobalVendorList as ITCFGlobalVendorListResponse } from "types/tcf";
import useTranslation from "./useTranslation";
import { NUMBER_0 } from "constants/numbers";

export const cachedGVL: { [lang: string]: ITCFGlobalVendorListResponse } = {};

const useLoadTCFGlobalVendorList = (): [
  ITCFGlobalVendorListResponse | undefined,
  boolean
] => {
  const { widgetConfig, setTcfVendorsList } = useAppState();
  const { userLang } = useTranslation();
  // show loading state only when not using cached data.
  const [isLoading, setIsLoading] = useState<boolean>(!cachedGVL[userLang]);
  const [data, setData] = useState<ITCFGlobalVendorListResponse | undefined>(
    UNDEFINED
  );

  useEffect(() => {
    loadGVL();
    // disabled eslint-rule below to run the effect only once, on mount
    // and when user's language changes
  }, [userLang]);

  const loadGVL = () => {
    return new Promise((resolve, reject) => {
      try {
        // use cached data if available
        if (cachedGVL[userLang]) {
          setData(cachedGVL[userLang]);
          setIsLoading(FALSE);
          return;
        }

        setData(UNDEFINED);
        setIsLoading(TRUE);
        let lang = userLang;
        if (!widgetConfig.tcfLanguages.includes(userLang.toString())) {
          lang = "en";
        }

        fetchTCFGlobalVendorList(widgetConfig.version, lang)
          .then((res) => {
            // Remove deleted vendors from vendor list
            let newGvl = { ...res };
            for (const key in res.vendors) {
              if ("deletedDate" in res.vendors[key]) {
                delete newGvl.vendors[key];
              }
            }

            cachedGVL[lang] = newGvl;
            setData(cachedGVL[lang]);

            // Check if there are any vendors specified in backend that are not in gvl.
            const newVendorsList: number[] = [];
            for (let i = NUMBER_0; i < widgetConfig.tcfVendors.length; i++) {
              if (newGvl.vendors[widgetConfig.tcfVendors[i]]) {
                newVendorsList.push(widgetConfig.tcfVendors[i]);
              }
            }

            setTcfVendorsList(newVendorsList);
          })
          .catch((err) => {
            console.error("Error fetching purposes:", err);
          })
          .finally(() => {
            setIsLoading(FALSE);
          });

        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  };

  return [data, isLoading];
};

export default useLoadTCFGlobalVendorList;
